import React, { useState, useEffect } from 'react';
import { financialApi, journalApi, projectApi } from '../services/api';
import { Card, Select, Spinner, Badge } from '../components/UI';
import { CurrencyDollar, TrendUp, TrendDown, ChartBar, Scales, ArrowUp, ArrowDown, CaretDown, CaretRight } from '@phosphor-icons/react';

import { fmtCurrency, fmtCurrencySigned } from '../utils/currencyFormat';

export default function FinancialDashboardPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [summary, setSummary] = useState(null);
  const [trialBalance, setTrialBalance] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [entryLines, setEntryLines] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { projectApi.list().then(r => setProjects(r.data)).finally(() => setLoading(false)); }, []);

  useEffect(() => {
    const pid = selectedProject || undefined;
    Promise.all([
      financialApi.summary(pid),
      journalApi.trialBalance(pid),
      journalApi.listEntries(pid)
    ]).then(([s, tb, je]) => {
      setSummary(s.data);
      setTrialBalance(tb.data);
      setJournalEntries(je.data);
    });
  }, [selectedProject]);

  const toggleEntry = async (entryId) => {
    if (expandedEntry === entryId) { setExpandedEntry(null); return; }
    setExpandedEntry(entryId);
    if (!entryLines[entryId]) {
      const res = await journalApi.getLines(entryId);
      setEntryLines(prev => ({ ...prev, [entryId]: res.data }));
    }
  };

  const fmt = (n) => fmtCurrency(n);
  const fmtSigned = (n) => fmtCurrencySigned(n);

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5" data-testid="financial-dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Financial Dashboard</h1>
          <p className="text-sm text-[#666]">Cost, revenue, profit/loss & accounting overview</p>
        </div>
        <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="w-64" data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="financial-kpis">
          <Card className="p-3 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendDown className="w-4 h-4 text-[#FF3333]" />
              <p className="text-[10px] text-[#999] uppercase">Total Cost</p>
            </div>
            <p className="text-xl font-bold text-[#FF3333]" data-testid="kpi-total-cost">{fmt(summary.total_cost)}</p>
          </Card>
          <Card className="p-3 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendUp className="w-4 h-4 text-[#00C853]" />
              <p className="text-[10px] text-[#999] uppercase">Revenue</p>
            </div>
            <p className="text-xl font-bold text-[#00C853]" data-testid="kpi-revenue">{fmt(summary.total_revenue)}</p>
          </Card>
          <Card className="p-3 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CurrencyDollar className="w-4 h-4 text-[#002FA7]" />
              <p className="text-[10px] text-[#999] uppercase">Profit/Loss</p>
            </div>
            <p className={`text-xl font-bold ${summary.profit_loss >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`} data-testid="kpi-profit">
              {fmtSigned(summary.profit_loss)}
            </p>
            {summary.margin_pct !== 0 && <p className="text-[10px] text-[#666]">{summary.margin_pct}% margin</p>}
          </Card>
          <Card className="p-3 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Scales className="w-4 h-4 text-[#FFAB00]" />
              <p className="text-[10px] text-[#999] uppercase">Outstanding</p>
            </div>
            <p className="text-xl font-bold text-[#FFAB00]" data-testid="kpi-outstanding">{fmt(summary.outstanding_receivable)}</p>
          </Card>
        </div>
      )}

      {/* Cost Breakdown + Payment Summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="p-4" data-testid="cost-breakdown">
            <h3 className="text-sm font-semibold text-[#0A0A0A] mb-2">Cost Breakdown</h3>
            {Object.keys(summary.costs_by_type).length === 0 ? (
              <p className="text-sm text-[#999]">No costs yet</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(summary.costs_by_type).map(([type, data]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-sm capitalize text-[#666]">{type}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#999]">{data.count} entries</span>
                      <span className="font-bold text-sm">{fmt(data.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card className="p-4" data-testid="payment-summary">
            <h3 className="text-sm font-semibold text-[#0A0A0A] mb-2">Revenue & Payments</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#666]">Invoiced</span><span className="font-bold">{fmt(summary.total_invoiced)}</span></div>
              <div className="flex justify-between"><span className="text-[#666]">Tax</span><span>{fmt(summary.total_tax)}</span></div>
              <div className="flex justify-between"><span className="text-[#666]">Payments Received</span><span className="font-bold text-[#00C853]">{fmt(summary.total_payments)}</span></div>
              <div className="flex justify-between border-t border-[#E0E0E0] pt-1"><span className="text-[#666]">Outstanding</span><span className="font-bold text-[#FF3333]">{fmt(summary.outstanding_receivable)}</span></div>
            </div>
          </Card>
        </div>
      )}

      {/* Trial Balance */}
      {trialBalance && trialBalance.accounts.length > 0 && (
        <Card className="p-4" data-testid="trial-balance">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-[#0A0A0A]">Trial Balance</h3>
            <Badge variant={trialBalance.is_balanced ? 'success' : 'danger'}>
              {trialBalance.is_balanced ? 'Balanced' : 'UNBALANCED'}
            </Badge>
          </div>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                  <th className="pb-2 pl-4 sm:pl-0">Account</th>
                  <th className="pb-2 text-right">Debit</th>
                  <th className="pb-2 text-right">Credit</th>
                  <th className="pb-2 text-right pr-4 sm:pr-0">Balance</th>
                </tr>
              </thead>
              <tbody>
                {trialBalance.accounts.map(a => (
                  <tr key={a.account} className="border-b border-[#F0F0F0]" data-testid={`tb-row-${a.account}`}>
                    <td className="py-1.5 pl-4 sm:pl-0 font-medium">{a.account}</td>
                    <td className="py-1.5 text-right text-[#666]">{a.total_debit > 0 ? fmt(a.total_debit) : '-'}</td>
                    <td className="py-1.5 text-right text-[#666]">{a.total_credit > 0 ? fmt(a.total_credit) : '-'}</td>
                    <td className={`py-1.5 text-right pr-4 sm:pr-0 font-bold ${a.balance > 0 ? 'text-[#002FA7]' : a.balance < 0 ? 'text-[#FF3333]' : ''}`}>
                      {fmtSigned(a.balance)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[#0A0A0A] font-bold">
                  <td className="py-2 pl-4 sm:pl-0">TOTAL</td>
                  <td className="py-2 text-right">{fmt(trialBalance.grand_total_debit)}</td>
                  <td className="py-2 text-right">{fmt(trialBalance.grand_total_credit)}</td>
                  <td className="py-2 text-right pr-4 sm:pr-0">{fmtSigned(trialBalance.grand_total_debit - trialBalance.grand_total_credit)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Recent Journal Entries */}
      {journalEntries.length > 0 && (
        <Card className="p-4" data-testid="journal-entries">
          <h3 className="text-sm font-semibold text-[#0A0A0A] mb-2">Recent Journal Entries</h3>
          <div className="space-y-1">
            {journalEntries.slice(0, 20).map(je => (
              <div key={je.id} className="border border-[#F0F0F0] rounded-lg overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-2.5 hover:bg-[#FAFAFA] text-left"
                  onClick={() => toggleEntry(je.id)}
                  data-testid={`je-row-${je.id}`}
                >
                  <div className="flex items-center gap-2">
                    {expandedEntry === je.id ? <CaretDown className="w-3 h-3 text-[#666]" /> : <CaretRight className="w-3 h-3 text-[#666]" />}
                    <span className="text-xs text-[#666]">{new Date(je.entry_date || je.created_at).toLocaleDateString()}</span>
                    <span className="text-sm">{je.description}</span>
                  </div>
                  <span className="text-xs font-mono text-[#002FA7]">{fmt(je.total_debit)}</span>
                </button>
                {expandedEntry === je.id && entryLines[je.id] && (
                  <div className="border-t border-[#F0F0F0] bg-[#FAFAFA] p-2">
                    <table className="w-full text-xs">
                      <tbody>
                        {entryLines[je.id].map(line => (
                          <tr key={line.id}>
                            <td className="py-0.5 pl-6">{line.account}</td>
                            <td className="py-0.5 text-right w-20">{line.debit > 0 ? fmt(line.debit) : ''}</td>
                            <td className="py-0.5 text-right w-20">{line.credit > 0 ? fmt(line.credit) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
